const timeStringToSeconds = (timeString) => {
  if (timeString === null || timeString === undefined || timeString === "") return 0;
  if (typeof timeString === "number" && Number.isFinite(timeString)) return timeString;

  const raw = String(timeString).trim();
  if (!raw) return 0;

  if (/^\d+(\.\d+)?$/.test(raw)) return Number(raw);

  const parts = raw.split(":").map((part) => Number(part));
  if (parts.some((part) => Number.isNaN(part))) return 0;

  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
};

const extractLectureFromTitle = (title = "") => {
  const lecMatch = title.match(/\blec[\s.-]*(\d+)\b/i);
  if (lecMatch) return `Lecture - ${lecMatch[1].padStart(2, "0")}`;

  const lectureMatch = title.match(/\blecture[\s.-]*(\d+)\b/i);
  if (lectureMatch) return `Lecture - ${lectureMatch[1].padStart(2, "0")}`;

  return null;
};

export const buildResumeVideoNavigationPayload = (video = {}) => ({
  ...video,
  video_id: video.video_id ?? video.videoId ?? video.id ?? null,
  redirectedFrom: "last_paused_video",
});

export const formatResumeSubtitle = (video = {}) => {
  if (video?.lecture_label) return video.lecture_label;

  const lectureNo =
    video?.lecture_no ??
    video?.lecture_number ??
    video?.lecture ??
    video?.lecture_id;
  if (lectureNo !== undefined && lectureNo !== null && lectureNo !== "") {
    return `Lecture - ${String(lectureNo).padStart(2, "0")}`;
  }

  const fromTitle = extractLectureFromTitle(video?.video_title || video?.title);
  if (fromTitle) return fromTitle;

  return video?.chapter_name || video?.subject || "Lecture";
};

export const getResumeVideoMetrics = (video = {}) => {
  const watched = Number(
    video?.last_paused ??
      video?.watched_duration ??
      video?.watched_time ??
      video?.watched_seconds ??
      0
  );

  let total = Number(video?.total_duration || 0);
  if (!total) total = timeStringToSeconds(video?.video_duration);
  if (!total) total = timeStringToSeconds(video?.duration);
  if (!total) total = Number(video?.video_duration || 0);

  const safeWatched = Number.isFinite(watched) ? Math.max(0, watched) : 0;
  const safeTotal = Number.isFinite(total) ? Math.max(0, total) : 0;

  const progress =
    safeTotal > 0
      ? Math.min(100, Math.max(0, Math.round((safeWatched / safeTotal) * 100)))
      : 0;

  const remainingSecs =
    safeTotal > safeWatched ? Math.max(0, safeTotal - safeWatched) : 0;
  const remainingMins =
    remainingSecs > 0 ? Math.max(1, Math.ceil(remainingSecs / 60)) : 0;

  return {
    progress,
    remainingMins,
    remainingSecs,
    remainingLabel: remainingMins
      ? `${remainingMins} mins remaining`
      : progress > 0
        ? "In progress"
        : "In progress",
  };
};

/**
 * Maps dashboardResumeActivity `data.resume_items` into ResumeSection cards.
 */
export const normalizeDashboardResumeActivity = (data) => {
  const list = Array.isArray(data?.resume_items)
    ? data.resume_items
    : Array.isArray(data)
      ? data
      : [];

  return list
    .map((raw, index) => {
      if (!raw || typeof raw !== "object") return null;

      const title = raw.test_name || raw.title;
      if (!title) return null;

      const remainingPercent = Math.min(
        100,
        Math.max(0, Math.round(Number(raw.remaining_percent) || 0))
      );
      const progress = Math.min(100, Math.max(0, 100 - remainingPercent));
      const actionLabel = raw.action_label || "Resume Test";
      const isPractice = String(actionLabel).toLowerCase().includes("practice");
      const paperId = raw.paper_id ?? null;
      const plannerTestId = Number(raw.planner_test_id) || 0;
      const testId = plannerTestId > 0 ? plannerTestId : paperId;
      const paperType = raw.paper_type ?? null;

      return {
        id: String(paperId ?? `resume-item-${index}`),
        type: isPractice ? "practice" : "test",
        title: String(title).trim(),
        subtitle: isPractice ? "Practice" : "Test",
        remainingLabel: `${remainingPercent}% remaining`,
        progress,
        actionLabel,
        payload: {
          resumeKind: "exam",
          paperId,
          testId,
          setId: null,
          paperType,
          practiceMode: isPractice ? "practice" : "test",
          plannerId: null,
          plannerTypeId: null,
          categoryId: "",
          raw: {
            ...raw,
            paper_id: paperId,
            test_id: testId,
            planner_test_id: plannerTestId || paperId,
            paper_type: paperType,
          },
        },
      };
    })
    .filter(Boolean);
};
